/**
 * 接続元（IP）の地域 → 出発地の都道府県コード。
 *
 * なぜブラウザの位置情報APIを使わないか:
 *  1) 許可ダイアログが出る。初回訪問でいきなり出すと拒否されるうえ、体験としても重い。
 *  2) そもそも呼べない ── public/_headers で `Permissions-Policy: geolocation=()` を宣言している
 *     （このヘッダを外すのは、位置情報を使わないという公開済みの説明を変えることになる）。
 *  → Cloudflare が付ける `request.cf`（IPジオロケーション）で「県の見当」だけを付ける。
 *
 * ⚠️ これは**推定**であって現在地の測定ではない。モバイル回線や社内プロキシでは実際の所在地と
 *    ずれる（日本のモバイルIPは東京に寄りやすい）。画面では「現在地から推定」と明示し、
 *    利用者が選び直したら以後はその選択を優先すること。推定を保存して既成事実にしない。
 */
import { DEPARTURE_PREFECTURES } from '../data/departure-prefectures';

/**
 * JIS X 0401 / ISO 3166-2:JP の都道府県番号順（01=北海道 … 47=沖縄）。
 * Cloudflare の `regionCode` が数字（"27"）で来る場合の引き当てに使う。
 * ★この並びが departure-prefectures.ts の並びと一致していることは scripts/test.ts で機械検査する
 *   （どちらかを触ったときに黙ってずれるのを防ぐ）。
 */
export const JP_PREF_CODES_IN_JIS_ORDER = [
  'hokkaido', 'aomori', 'iwate', 'miyagi', 'akita', 'yamagata', 'fukushima',
  'ibaraki', 'tochigi', 'gunma', 'saitama', 'chiba', 'tokyo', 'kanagawa',
  'niigata', 'toyama', 'ishikawa', 'fukui', 'yamanashi', 'nagano', 'gifu', 'shizuoka', 'aichi',
  'mie', 'shiga', 'kyoto', 'osaka', 'hyogo', 'nara', 'wakayama',
  'tottori', 'shimane', 'okayama', 'hiroshima', 'yamaguchi',
  'tokushima', 'kagawa', 'ehime', 'kochi',
  'fukuoka', 'saga', 'nagasaki', 'kumamoto', 'oita', 'miyazaki', 'kagoshima',
  'okinawa',
] as const;

/**
 * Cloudflare が返す地域名の異表記。
 * `region` は英語のローマ字名（"Ōsaka" のように長音符が付くことがある）で来る。
 * 長音符は正規化で落とせるが、**綴りそのものが違う**ものだけをここに書く。
 */
const NAME_ALIASES: Record<string, string> = {
  gumma: 'gunma', // 群馬は Gunma / Gumma の両綴りが流通する
  tokyoto: 'tokyo',
  osakafu: 'osaka',
  kyotofu: 'kyoto',
};

/** 長音符・記号・大小の揺れを落として比較用の綴りにする（"Ōsaka-fu" → "osakafu"）。 */
function normalizeName(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // 結合ダイアクリティカルマーク（Ō の長音符など）
    .toLowerCase()
    .replace(/[^a-z]/g, '');
}

const NAME_TO_CODE = new Map<string, string>(JP_PREF_CODES_IN_JIS_ORDER.map((c) => [c, c]));

export interface CfGeoLike {
  /** ISO 3166-1 alpha-2（"JP"） */
  country?: string | null;
  /** 地域名（"Ōsaka" / "Tokyo"） */
  region?: string | null;
  /** 地域コード。日本は ISO 3166-2:JP の数字（"27"）で来る想定だが "JP-27" 等も受ける */
  regionCode?: string | null;
}

export type GeoSource = 'regionCode' | 'region' | 'none' | 'non-jp';

/**
 * `request.cf` から都道府県コードを引く。引けなければ null（＝既定の東京のままにする）。
 * 日本国外からの接続では推定しない（海外在住者に勝手な国内出発地を入れない）。
 */
export function prefCodeFromCfGeo(cf: CfGeoLike | null | undefined): { pref: string | null; source: GeoSource } {
  if (!cf) return { pref: null, source: 'none' };
  const country = (cf.country || '').toUpperCase();
  if (country && country !== 'JP') return { pref: null, source: 'non-jp' };

  // 1) 地域コード（数字）。ISO 3166-2:JP は 01〜47 が JIS の都道府県番号と一致する。
  const rawCode = (cf.regionCode || '').trim().replace(/^JP-/i, '');
  if (/^\d{1,2}$/.test(rawCode)) {
    const n = Number(rawCode);
    if (n >= 1 && n <= 47) return { pref: JP_PREF_CODES_IN_JIS_ORDER[n - 1]!, source: 'regionCode' };
  }
  // 2) 地域名（ローマ字）。regionCode が英字2文字などで来ても、名前側で拾える。
  for (const raw of [cf.region, rawCode]) {
    if (!raw) continue;
    const key = normalizeName(raw);
    if (!key) continue;
    const hit = NAME_TO_CODE.get(key) ?? NAME_TO_CODE.get(NAME_ALIASES[key] ?? '');
    if (hit) return { pref: hit, source: 'region' };
    // "Osaka Prefecture" のような接尾辞つきは前方一致で拾う（最長一致を採る）
    let best = '';
    for (const code of JP_PREF_CODES_IN_JIS_ORDER) {
      if (key.startsWith(code) && code.length > best.length) best = code;
    }
    if (best) return { pref: best, source: 'region' };
  }
  return { pref: null, source: 'none' };
}

/** 出発地マスタに実在するコードだけを通す（マスタから県が消えても壊れないように）。 */
export function isKnownDeparturePref(code: string | null | undefined): boolean {
  return !!code && DEPARTURE_PREFECTURES.some((p) => p.code === code);
}
